import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  Shield,
  Home,
  Users,
  Sparkles,
  MessageSquare,
  DollarSign,
  BarChart3,
  Settings,
  Image,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Save,
  Eye,
  Loader2,
  ChevronDown,
  AlertTriangle,
  GripVertical,
  X,
  Upload
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("hero");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Content States
  const [hero, setHero] = useState(null);
  const [team, setTeam] = useState([]);
  const [features, setFeatures] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [stats, setStats] = useState([]);
  const [problems, setProblems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [images, setImages] = useState([]);
  
  // Dialog States
  const [editDialog, setEditDialog] = useState({ open: false, type: null, data: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
  });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchAllContent();
  }, [navigate]);

  const fetchAllContent = async () => {
    setLoading(true);
    try {
      const [heroRes, teamRes, featuresRes, testimonialsRes, pricingRes, statsRes, problemsRes, settingsRes, imagesRes] = await Promise.all([
        axios.get(`${API}/cms/hero`),
        axios.get(`${API}/cms/team`),
        axios.get(`${API}/cms/features`),
        axios.get(`${API}/cms/testimonials`),
        axios.get(`${API}/cms/pricing`),
        axios.get(`${API}/cms/stats`),
        axios.get(`${API}/cms/problems`),
        axios.get(`${API}/cms/settings`),
        axios.get(`${API}/cms/images`, getAuthHeader()).catch(() => ({ data: [] }))
      ]);
      
      setHero(heroRes.data);
      setTeam(teamRes.data);
      setFeatures(featuresRes.data);
      setTestimonials(testimonialsRes.data);
      setPricing(pricingRes.data);
      setStats(statsRes.data);
      setProblems(problemsRes.data);
      setSettings(settingsRes.data);
      setImages(imagesRes.data);
    } catch (error) {
      console.error("Error fetching content:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("adminToken");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin");
  };

  // Save Functions
  const saveHero = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/hero`, hero, getAuthHeader());
      toast.success("Hero section updated!");
    } catch (error) {
      toast.error("Failed to save hero section");
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/settings`, settings, getAuthHeader());
      toast.success("Settings updated!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // CRUD Operations
  const handleAdd = async (type, data) => {
    try {
      await axios.post(`${API}/cms/${type}`, data, getAuthHeader());
      toast.success(`${type} added successfully!`);
      fetchAllContent();
      setEditDialog({ open: false, type: null, data: null });
    } catch (error) {
      toast.error(`Failed to add ${type}`);
    }
  };

  const handleUpdate = async (type, id, data) => {
    try {
      await axios.put(`${API}/cms/${type}/${id}`, data, getAuthHeader());
      toast.success(`${type} updated successfully!`);
      fetchAllContent();
      setEditDialog({ open: false, type: null, data: null });
    } catch (error) {
      toast.error(`Failed to update ${type}`);
    }
  };

  const handleDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      await axios.delete(`${API}/cms/${type}/${id}`, getAuthHeader());
      toast.success(`Deleted successfully!`);
      fetchAllContent();
      setDeleteDialog({ open: false, type: null, id: null });
    } catch (error) {
      toast.error(`Failed to delete`);
    }
  };

  // Image Upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64 = reader.result;
        await axios.post(`${API}/cms/upload`, {
          filename: file.name,
          data: base64
        }, getAuthHeader());
        toast.success("Image uploaded!");
        fetchAllContent();
      } catch (error) {
        toast.error("Failed to upload image");
      }
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#7C3AED]" />
      </div>
    );
  }

  const menuItems = [
    { id: "hero", label: "Hero Section", icon: Home },
    { id: "team", label: "Team Members", icon: Users },
    { id: "features", label: "Features", icon: Sparkles },
    { id: "testimonials", label: "Testimonials", icon: MessageSquare },
    { id: "pricing", label: "Pricing", icon: DollarSign },
    { id: "stats", label: "Statistics", icon: BarChart3 },
    { id: "problems", label: "Problems", icon: AlertTriangle },
    { id: "images", label: "Media Library", icon: Image },
    { id: "settings", label: "Site Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#1E3A8A] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#7C3AED]" />
              <span className="font-bold text-lg">readBest Admin</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/" target="_blank">
                <Button variant="ghost" className="text-white hover:bg-white/10">
                  <Eye className="w-4 h-4 mr-2" />
                  View Site
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-white hover:bg-white/10">
                    <div className="w-8 h-8 rounded-full bg-[#7C3AED] flex items-center justify-center mr-2">
                      RG
                    </div>
                    Admin
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="grid md:grid-cols-[250px_1fr] gap-8">
          {/* Sidebar */}
          <Card className="h-fit sticky top-24">
            <CardContent className="p-4">
              <nav className="space-y-1">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeTab === item.id
                        ? "bg-[#7C3AED] text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </button>
                ))}
              </nav>
            </CardContent>
          </Card>

          {/* Main Content */}
          <div className="space-y-6">
            {/* Hero Section Editor */}
            {activeTab === "hero" && hero && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    Hero Section
                  </CardTitle>
                  <CardDescription>Edit the main hero section of your landing page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label>Badge Text</Label>
                        <Input
                          value={hero.badge_text}
                          onChange={(e) => setHero({ ...hero, badge_text: e.target.value })}
                          placeholder="Badge text"
                        />
                      </div>
                      <div>
                        <Label>Headline</Label>
                        <Input
                          value={hero.headline}
                          onChange={(e) => setHero({ ...hero, headline: e.target.value })}
                          placeholder="Main headline"
                        />
                      </div>
                      <div>
                        <Label>Headline Highlight (colored text)</Label>
                        <Input
                          value={hero.headline_highlight}
                          onChange={(e) => setHero({ ...hero, headline_highlight: e.target.value })}
                          placeholder="Highlighted text"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={hero.description}
                          onChange={(e) => setHero({ ...hero, description: e.target.value })}
                          placeholder="Hero description"
                          rows={4}
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label>Hero Image URL</Label>
                        <Input
                          value={hero.hero_image}
                          onChange={(e) => setHero({ ...hero, hero_image: e.target.value })}
                          placeholder="https://..."
                        />
                        {hero.hero_image && (
                          <img src={hero.hero_image} alt="Hero preview" className="mt-2 rounded-lg max-h-40 object-cover" />
                        )}
                      </div>
                      <div>
                        <Label>Primary CTA Text</Label>
                        <Input
                          value={hero.cta_primary}
                          onChange={(e) => setHero({ ...hero, cta_primary: e.target.value })}
                          placeholder="Button text"
                        />
                      </div>
                      <div>
                        <Label>Secondary CTA Text</Label>
                        <Input
                          value={hero.cta_secondary}
                          onChange={(e) => setHero({ ...hero, cta_secondary: e.target.value })}
                          placeholder="Button text"
                        />
                      </div>
                    </div>
                  </div>
                  <Button onClick={saveHero} disabled={saving} className="bg-[#7C3AED] hover:bg-[#6D28D9]">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Hero Section
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Team Members Editor */}
            {activeTab === "team" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Team Members
                    </CardTitle>
                    <CardDescription>Manage your team members</CardDescription>
                  </div>
                  <Button onClick={() => setEditDialog({ open: true, type: "team", data: null })} className="bg-[#7C3AED] hover:bg-[#6D28D9]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {team.map((member) => (
                      <Card key={member.id} className="relative group">
                        <CardContent className="p-4 text-center">
                          <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#1E3A8A] flex items-center justify-center text-white text-xl font-bold">
                            {member.initial}
                          </div>
                          <h4 className="font-semibold">{member.name}</h4>
                          <p className="text-sm text-[#7C3AED]">{member.role}</p>
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => setEditDialog({ open: true, type: "team", data: member })}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-red-500" onClick={() => setDeleteDialog({ open: true, type: "team", id: member.id })}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Features Editor */}
            {activeTab === "features" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      Features
                    </CardTitle>
                    <CardDescription>Manage product features</CardDescription>
                  </div>
                  <Button onClick={() => setEditDialog({ open: true, type: "features", data: null })} className="bg-[#7C3AED] hover:bg-[#6D28D9]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Feature
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {features.map((feature) => (
                      <div key={feature.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg group">
                        <div className="flex items-center gap-4">
                          <GripVertical className="w-5 h-5 text-gray-400" />
                          <div>
                            <h4 className="font-semibold">{feature.title}</h4>
                            <p className="text-sm text-gray-500">{feature.description.substring(0, 60)}...</p>
                          </div>
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button size="sm" variant="ghost" onClick={() => setEditDialog({ open: true, type: "features", data: feature })}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteDialog({ open: true, type: "features", id: feature.id })}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Testimonials Editor */}
            {activeTab === "testimonials" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Testimonials
                    </CardTitle>
                    <CardDescription>Manage customer testimonials</CardDescription>
                  </div>
                  <Button onClick={() => setEditDialog({ open: true, type: "testimonials", data: null })} className="bg-[#7C3AED] hover:bg-[#6D28D9]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Testimonial
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {testimonials.map((testimonial) => (
                      <Card key={testimonial.id} className="relative group">
                        <CardContent className="p-4">
                          <p className="italic text-gray-600 mb-3">"{testimonial.quote.substring(0, 100)}..."</p>
                          <div>
                            <p className="font-semibold">{testimonial.name}</p>
                            <p className="text-sm text-[#7C3AED]">{testimonial.role}</p>
                          </div>
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => setEditDialog({ open: true, type: "testimonials", data: testimonial })}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-red-500" onClick={() => setDeleteDialog({ open: true, type: "testimonials", id: testimonial.id })}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pricing Editor */}
            {activeTab === "pricing" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Pricing Tiers
                    </CardTitle>
                    <CardDescription>Manage pricing plans</CardDescription>
                  </div>
                  <Button onClick={() => setEditDialog({ open: true, type: "pricing", data: null })} className="bg-[#7C3AED] hover:bg-[#6D28D9]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Tier
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    {pricing.map((tier) => (
                      <Card key={tier.id} className={`relative group ${tier.highlighted ? 'border-[#7C3AED] border-2' : ''}`}>
                        <CardContent className="p-4">
                          {tier.highlighted && <span className="text-xs bg-[#7C3AED] text-white px-2 py-1 rounded-full">Popular</span>}
                          <h4 className="font-bold text-xl mt-2">{tier.name}</h4>
                          <p className="text-3xl font-bold text-[#1E3A8A]">{tier.price}<span className="text-sm text-gray-500">{tier.period}</span></p>
                          <ul className="mt-3 space-y-1">
                            {tier.features.slice(0, 3).map((f, i) => (
                              <li key={i} className="text-sm text-gray-600">• {f}</li>
                            ))}
                          </ul>
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => setEditDialog({ open: true, type: "pricing", data: tier })}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-red-500" onClick={() => setDeleteDialog({ open: true, type: "pricing", id: tier.id })}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Statistics Editor */}
            {activeTab === "stats" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Statistics
                    </CardTitle>
                    <CardDescription>Manage market statistics</CardDescription>
                  </div>
                  <Button onClick={() => setEditDialog({ open: true, type: "stats", data: null })} className="bg-[#7C3AED] hover:bg-[#6D28D9]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stat
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    {stats.map((stat) => (
                      <Card key={stat.id} className="relative group bg-gradient-to-br from-[#7C3AED] to-[#1E3A8A] text-white">
                        <CardContent className="p-6 text-center">
                          <p className="text-3xl font-bold">{stat.value}</p>
                          <p className="text-purple-100 text-sm">{stat.label}</p>
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                            <Button size="icon" variant="ghost" className="text-white hover:bg-white/20" onClick={() => setEditDialog({ open: true, type: "stats", data: stat })}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-white hover:bg-white/20" onClick={() => setDeleteDialog({ open: true, type: "stats", id: stat.id })}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Problems Editor */}
            {activeTab === "problems" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Problems We Solve
                    </CardTitle>
                    <CardDescription>Manage problem statements</CardDescription>
                  </div>
                  <Button onClick={() => setEditDialog({ open: true, type: "problems", data: null })} className="bg-[#7C3AED] hover:bg-[#6D28D9]">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Problem
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {problems.map((problem, index) => (
                      <div key={problem.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg group">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-[#7C3AED] flex items-center justify-center text-white font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <h4 className="font-semibold">{problem.title}</h4>
                            <p className="text-sm text-gray-500">{problem.description.substring(0, 60)}...</p>
                          </div>
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button size="sm" variant="ghost" onClick={() => setEditDialog({ open: true, type: "problems", data: problem })}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteDialog({ open: true, type: "problems", id: problem.id })}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Media Library */}
            {activeTab === "images" && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Image className="w-5 h-5" />
                      Media Library
                    </CardTitle>
                    <CardDescription>Upload and manage images</CardDescription>
                  </div>
                  <label className="cursor-pointer">
                    <Button className="bg-[#7C3AED] hover:bg-[#6D28D9]" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Image
                      </span>
                    </Button>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {images.map((img) => (
                      <div key={img.id} className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        <img src={`${API}/cms/images/${img.id}`} alt={img.filename} className="w-full h-full object-cover" onError={(e) => e.target.src = 'https://via.placeholder.com/150'} />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button size="icon" variant="ghost" className="text-white" onClick={() => setDeleteDialog({ open: true, type: "images", id: img.id })}>
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>
                        <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">{img.filename}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Site Settings */}
            {activeTab === "settings" && settings && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Site Settings
                  </CardTitle>
                  <CardDescription>Configure global site settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label>Site Name</Label>
                        <Input
                          value={settings.site_name}
                          onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Tagline</Label>
                        <Input
                          value={settings.tagline}
                          onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Logo Primary Text</Label>
                        <Input
                          value={settings.logo_text_primary}
                          onChange={(e) => setSettings({ ...settings, logo_text_primary: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Logo Secondary Text</Label>
                        <Input
                          value={settings.logo_text_secondary}
                          onChange={(e) => setSettings({ ...settings, logo_text_secondary: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label>Primary Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={settings.primary_color}
                            onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                            className="w-16 h-10 p-1"
                          />
                          <Input
                            value={settings.primary_color}
                            onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Secondary Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={settings.secondary_color}
                            onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                            className="w-16 h-10 p-1"
                          />
                          <Input
                            value={settings.secondary_color}
                            onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Footer Text</Label>
                        <Input
                          value={settings.footer_text}
                          onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <Label>Show SDG Badge</Label>
                        <Switch
                          checked={settings.sdg_badge_visible}
                          onCheckedChange={(checked) => setSettings({ ...settings, sdg_badge_visible: checked })}
                        />
                      </div>
                    </div>
                  </div>
                  <Button onClick={saveSettings} disabled={saving} className="bg-[#7C3AED] hover:bg-[#6D28D9]">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Settings
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <EditItemDialog
        open={editDialog.open}
        type={editDialog.type}
        data={editDialog.data}
        onClose={() => setEditDialog({ open: false, type: null, data: null })}
        onSave={(data) => {
          if (editDialog.data) {
            handleUpdate(editDialog.type, editDialog.data.id, data);
          } else {
            handleAdd(editDialog.type, data);
          }
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: null, id: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this item? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: null, id: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Edit Item Dialog Component
const EditItemDialog = ({ open, type, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (data) {
      setFormData(data);
    } else {
      // Default values for new items
      const defaults = {
        team: { name: "", role: "", initial: "", order: 0 },
        features: { icon: "Sparkles", title: "", description: "", order: 0 },
        testimonials: { quote: "", name: "", role: "", description: "", rating: 5, order: 0 },
        pricing: { name: "", price: "", period: "", features: [], cta: "", highlighted: false, order: 0 },
        stats: { value: "", label: "", order: 0 },
        problems: { title: "", description: "", order: 0 }
      };
      setFormData(defaults[type] || {});
    }
  }, [data, type]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const renderFields = () => {
    switch (type) {
      case "team":
        return (
          <>
            <div><Label>Name</Label><Input value={formData.name || ""} onChange={(e) => handleChange("name", e.target.value)} /></div>
            <div><Label>Role</Label><Input value={formData.role || ""} onChange={(e) => handleChange("role", e.target.value)} /></div>
            <div><Label>Initials</Label><Input value={formData.initial || ""} onChange={(e) => handleChange("initial", e.target.value)} maxLength={2} /></div>
            <div><Label>Order</Label><Input type="number" value={formData.order || 0} onChange={(e) => handleChange("order", parseInt(e.target.value))} /></div>
          </>
        );
      case "features":
        return (
          <>
            <div><Label>Icon (Lucide name)</Label><Input value={formData.icon || ""} onChange={(e) => handleChange("icon", e.target.value)} placeholder="e.g., Brain, Sparkles, Volume2" /></div>
            <div><Label>Title</Label><Input value={formData.title || ""} onChange={(e) => handleChange("title", e.target.value)} /></div>
            <div><Label>Description</Label><Textarea value={formData.description || ""} onChange={(e) => handleChange("description", e.target.value)} /></div>
            <div><Label>Order</Label><Input type="number" value={formData.order || 0} onChange={(e) => handleChange("order", parseInt(e.target.value))} /></div>
          </>
        );
      case "testimonials":
        return (
          <>
            <div><Label>Quote</Label><Textarea value={formData.quote || ""} onChange={(e) => handleChange("quote", e.target.value)} /></div>
            <div><Label>Name</Label><Input value={formData.name || ""} onChange={(e) => handleChange("name", e.target.value)} /></div>
            <div><Label>Role</Label><Input value={formData.role || ""} onChange={(e) => handleChange("role", e.target.value)} /></div>
            <div><Label>Description</Label><Input value={formData.description || ""} onChange={(e) => handleChange("description", e.target.value)} /></div>
            <div><Label>Rating (1-5)</Label><Input type="number" min={1} max={5} value={formData.rating || 5} onChange={(e) => handleChange("rating", parseInt(e.target.value))} /></div>
          </>
        );
      case "pricing":
        return (
          <>
            <div><Label>Tier Name</Label><Input value={formData.name || ""} onChange={(e) => handleChange("name", e.target.value)} /></div>
            <div><Label>Price</Label><Input value={formData.price || ""} onChange={(e) => handleChange("price", e.target.value)} placeholder="e.g., $5 or Free" /></div>
            <div><Label>Period</Label><Input value={formData.period || ""} onChange={(e) => handleChange("period", e.target.value)} placeholder="e.g., /month" /></div>
            <div><Label>Features (one per line)</Label><Textarea value={(formData.features || []).join("\n")} onChange={(e) => handleChange("features", e.target.value.split("\n"))} rows={5} /></div>
            <div><Label>CTA Button Text</Label><Input value={formData.cta || ""} onChange={(e) => handleChange("cta", e.target.value)} /></div>
            <div className="flex items-center gap-2"><Switch checked={formData.highlighted || false} onCheckedChange={(checked) => handleChange("highlighted", checked)} /><Label>Highlighted (Popular)</Label></div>
          </>
        );
      case "stats":
        return (
          <>
            <div><Label>Value</Label><Input value={formData.value || ""} onChange={(e) => handleChange("value", e.target.value)} placeholder="e.g., $53.6B" /></div>
            <div><Label>Label</Label><Input value={formData.label || ""} onChange={(e) => handleChange("label", e.target.value)} /></div>
            <div><Label>Order</Label><Input type="number" value={formData.order || 0} onChange={(e) => handleChange("order", parseInt(e.target.value))} /></div>
          </>
        );
      case "problems":
        return (
          <>
            <div><Label>Title</Label><Input value={formData.title || ""} onChange={(e) => handleChange("title", e.target.value)} /></div>
            <div><Label>Description</Label><Textarea value={formData.description || ""} onChange={(e) => handleChange("description", e.target.value)} /></div>
            <div><Label>Order</Label><Input type="number" value={formData.order || 0} onChange={(e) => handleChange("order", parseInt(e.target.value))} /></div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{data ? "Edit" : "Add"} {type}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {renderFields()}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} className="bg-[#7C3AED] hover:bg-[#6D28D9]">
            <Save className="w-4 h-4 mr-2" />
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;
